import React, { useState, useEffect } from 'react';
import { useAuth, PERMISSIONS } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import logo from '../logo.svg';
import { FiUser, FiLock, FiEye, FiEyeOff, FiLogIn, FiCalendar, FiShield, FiTrendingUp } from 'react-icons/fi';

const LoginPage = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { t } = useTranslation();
  const { handleTraditionalLogin, setAuthenticatedUser } = useAuth();
  const navigate = useNavigate();

  const handleGoogleCredential = React.useCallback(async (response) => {
    try {
      setLoading(true);
      setError('');
      if (!response?.credential) throw new Error('No credential received from Google');
      const syncResponse = await api.post('/auth/google-sync', { credential: response.credential });
      if (syncResponse.data.success) {
        if (syncResponse.data.data?.accessToken) {
          localStorage.setItem('accessToken', syncResponse.data.data.accessToken);
        }
        const user = syncResponse.data.data?.user;
        const accessToken = syncResponse.data.data?.accessToken;
        const refreshToken = syncResponse.data.data?.refreshToken;
        if (user && accessToken) {
          setAuthenticatedUser(user, accessToken, refreshToken);
          navigate(determineRedirectPath(user), { replace: true });
        } else {
          throw new Error('No user data or access token received from Google sync');
        }
      } else {
        throw new Error(syncResponse.data.message || 'Google sync failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur de connexion via Google');
    } finally {
      setLoading(false);
    }
  }, [navigate, setAuthenticatedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    if (window.google) {
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredential,
          ux_mode: 'popup',
          context: 'signin'
        });
        const buttonContainer = document.getElementById('gsi-button-container');
        if (buttonContainer) {
          window.google.accounts.id.renderButton(buttonContainer, {
            theme: 'outline', size: 'large', text: 'continue_with', width: '320', type: 'standard'
          });
        }
      } catch (e) {}
    }
  }, [handleGoogleCredential]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await handleTraditionalLogin(formData);
      if (result.success) {
        const user = result.user;
        if (!user.isActive) { setError(t('loginAccountInactive')); setLoading(false); return; }
        if (user.Account && !user.Account.isActive) { setError(t('loginOrganizationInactive')); setLoading(false); return; }
        setTimeout(() => navigate(determineRedirectPath(user), { replace: true }), 100);
      } else {
        setError(formatLoginError(result.error || result.message || 'Erreur de connexion'));
      }
    } catch (err) {
      setError(formatLoginError(err.response?.data?.message || err.message || 'Erreur de connexion'));
    } finally {
      setLoading(false);
    }
  };

  const formatLoginError = (message) => {
    if (!message) return t('error');
    const lower = message.toLowerCase();
    if (lower.includes('invalid username or password') || lower.includes('identifiants'))
      return t('loginErrorInvalidCredentials');
    if (lower.includes('username and password are required'))
      return t('loginErrorMissingFields');
    if (lower.includes('abonnement est inactif') || lower.includes('account is inactive'))
      return t('loginErrorInactiveSubscription');
    if (lower.includes('authentication system error'))
      return t('loginErrorAuthenticationSystem');
    if (lower.includes('network') || lower.includes('failed to fetch'))
      return t('loginErrorNetworkConnection');
    return message;
  };

  const determineRedirectPath = (user) => {
    if (user.defaultPage) return user.defaultPage;
    if (user.isMasterAdmin) return '/dashboard';
    const userPermissions = user.Permissions?.map(p => p.keyName) || [];
    const pagePriority = [
      { permission: PERMISSIONS.VIEW_DASHBOARD, path: '/dashboard' },
      { permission: PERMISSIONS.VIEW_OVERVIEW, path: '/overview' },
      { permission: PERMISSIONS.VIEW_CARS, path: '/voitures' },
      { permission: PERMISSIONS.VIEW_RESERVATIONS, path: '/contracts' },
      { permission: PERMISSIONS.VIEW_CLIENTS, path: '/clients' },
      { permission: PERMISSIONS.VIEW_MAINTENANCE, path: '/maintenance' },
      { permission: PERMISSIONS.VIEW_REPORTS, path: '/reports' },
      { permission: PERMISSIONS.VIEW_USERS, path: '/users' }
    ];
    for (const page of pagePriority) {
      if (userPermissions.includes(page.permission)) return page.path;
    }
    return '/voitures';
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }

        .lp-root {
          min-height: 100vh;
          position: relative;
          overflow: hidden;
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #06060E;
          color: #F2F0FA;
        }

        /* ── Showroom photo backdrop — the car, with two stacked gradients
             for legibility: a vertical vignette (nav/footer) and a horizontal
             one biased dark-left where the headline text sits with nothing
             behind it. ─── */
        .lp-photo-bg {
          position: absolute; inset: 0; z-index: 0;
          background:
            linear-gradient(180deg, rgba(6,6,14,0.80) 0%, rgba(6,6,14,0.10) 20%, rgba(6,6,14,0.10) 76%, rgba(6,6,14,0.88) 100%),
            linear-gradient(100deg, rgba(6,6,14,0.95) 0%, rgba(6,6,14,0.72) 34%, rgba(6,6,14,0.30) 58%, rgba(6,6,14,0.58) 100%),
            url('/images/login/kardian-showroom.jpg') center 42% / cover no-repeat;
        }
        @media (max-width: 900px) {
          .lp-photo-bg {
            background:
              linear-gradient(180deg, rgba(6,6,14,0.85) 0%, rgba(6,6,14,0.35) 30%, rgba(6,6,14,0.55) 65%, rgba(6,6,14,0.92) 100%),
              url('/images/login/kardian-showroom.jpg') center 30% / cover no-repeat;
          }
        }

        /* ── Top nav bar ─── */
        .lp-nav {
          position: absolute; top: 0; left: 0; right: 0;
          padding: 24px 40px;
          display: flex; justify-content: space-between; align-items: center;
          z-index: 10;
        }
        @media (max-width: 640px) { .lp-nav { padding: 18px 22px; } }

        .lp-nav-brand {
          display: flex; align-items: center; gap: 10px;
        }
        .lp-nav-brand img { height: 32px; width: auto; filter: brightness(0) invert(1); }
        .lp-nav-brand-name { font-family: 'Outfit', sans-serif; font-size: 19px; font-weight: 800; color: #fff; letter-spacing: -0.02em; }

        /* ── Main two-column layout ─── */
        .lp-body {
          position: absolute; inset: 0;
          padding: 100px 60px 50px;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 5;
        }
        .lp-body-inner {
          width: 100%;
          max-width: 1360px;
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 60px;
          align-items: center;
        }
        @media (max-width: 900px) {
          .lp-body { padding: 90px 22px 30px; align-items: flex-start; }
          .lp-body-inner { grid-template-columns: 1fr; }
          .lp-headline-col { display: none; }
        }

        /* ── Left headline col ─── */
        .lp-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 11.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
          color: #FFB37A;
          background: rgba(255,179,122,0.12);
          border: 1px solid rgba(255,179,122,0.3);
          padding: 6px 14px; border-radius: 999px; margin-bottom: 22px;
        }
        .lp-headline {
          font-family: 'Outfit', sans-serif;
          font-size: clamp(42px, 6vw, 76px);
          font-weight: 800;
          line-height: 0.98;
          letter-spacing: -0.03em;
          margin: 0;
          color: #fff;
          text-shadow: 0 4px 30px rgba(0,0,0,0.35);
        }
        .lp-headline-sub {
          margin-top: 24px; font-size: 16px; line-height: 1.65;
          color: rgba(242,240,250,0.72); max-width: 440px;
        }
        .lp-features {
          display: flex; flex-direction: column; gap: 14px; margin-top: 40px;
        }
        .lp-feature {
          display: flex; align-items: center; gap: 12px;
          font-size: 14.5px; font-weight: 500; color: #fff;
        }
        .lp-feature-icon {
          width: 30px; height: 30px; border-radius: 10px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.14); color: #FFB37A;
        }

        /* ── Form card — frosted dark glass over the photo ─── */
        .lp-card {
          background: rgba(15,13,26,0.58);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 26px;
          padding: 32px;
          box-shadow: 0 24px 60px -16px rgba(0,0,0,0.55);
          position: relative;
        }
        .lp-card-title {
          font-size: 20px; font-weight: 800;
          letter-spacing: -0.01em;
          color: #fff;
        }
        .lp-card-subtitle {
          margin-top: 4px; font-size: 13.5px; color: rgba(242,240,250,0.62);
        }
        .lp-card-body { margin-top: 22px; display: flex; flex-direction: column; gap: 16px; }

        /* inputs */
        .lp-field { display: flex; flex-direction: column; gap: 0; }
        .lp-label {
          font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
          text-transform: uppercase; color: rgba(242,240,250,0.55);
          margin-bottom: 7px; font-family: inherit;
        }
        .lp-input-wrap { position: relative; }
        .lp-input-icon {
          position: absolute; left: 14px; top: 50%;
          transform: translateY(-50%);
          color: rgba(242,240,250,0.45); pointer-events: none; display: flex;
        }
        .lp-input {
          width: 100%;
          padding: 13px 14px 13px 42px;
          font-size: 14.5px;
          border: 1.5px solid rgba(255,255,255,0.16);
          border-radius: 15px;
          background: rgba(255,255,255,0.06);
          color: #fff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
          appearance: none;
          font-family: inherit;
        }
        .lp-input::placeholder { color: rgba(242,240,250,0.4); }
        .lp-input:focus {
          border-color: #8B7FF0;
          box-shadow: 0 0 0 4px rgba(139,127,240,0.2);
          background: rgba(255,255,255,0.1);
        }
        .lp-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .lp-eye-btn {
          position: absolute; right: 12px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: rgba(242,240,250,0.45); padding: 4px;
          display: flex; align-items: center; transition: color 0.2s;
        }
        .lp-eye-btn:hover { color: #fff; }

        /* error */
        .lp-error {
          display: flex; align-items: flex-start; gap: 10px;
          background: rgba(225,72,72,0.16);
          border: 1px solid rgba(225,72,72,0.35);
          border-radius: 12px;
          padding: 11px 13px;
          font-size: 13px; color: #FF9E9E; line-height: 1.5;
        }
        .lp-error svg { flex-shrink: 0; margin-top: 1px; }

        /* Google button */
        .lp-google-btn {
          width: 100%; padding: 12px 16px; border-radius: 15px;
          background: #fff; color: #1f2937;
          font-weight: 700; font-size: 14.5px;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 8px 22px -10px rgba(0,0,0,0.5);
          cursor: pointer; transition: opacity 0.15s; font-family: inherit;
        }
        .lp-google-btn:hover { opacity: 0.92; }
        .lp-google-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* divider */
        .lp-divider {
          display: flex; align-items: center; gap: 10px;
          font-size: 11px; font-weight: 600;
          letter-spacing: 0.1em; text-transform: uppercase;
          color: rgba(242,240,250,0.4);
        }
        .lp-divider::before, .lp-divider::after {
          content: ''; flex: 1; height: 1px;
          background: rgba(255,255,255,0.14);
        }

        /* submit button */
        .lp-btn {
          width: 100%; padding: 14px 20px;
          border-radius: 15px;
          background: linear-gradient(135deg, #4F3EE0 0%, #FF6B4A 100%); color: #fff;
          font-weight: 700; font-size: 15px;
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          transition: transform 0.15s, box-shadow 0.15s;
          box-shadow: 0 10px 26px -6px rgba(255,107,74,0.45);
          font-family: inherit;
          margin-top: 4px;
        }
        .lp-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 30px -6px rgba(255,107,74,0.55); }
        .lp-btn:active:not(:disabled) { transform: translateY(0); }
        .lp-btn:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }

        /* forgot / access row */
        .lp-links {
          display: flex; justify-content: space-between;
          font-size: 12.5px; color: rgba(242,240,250,0.6);
        }
        .lp-links a { color: inherit; text-decoration: none; cursor: pointer; font-weight: 500; }
        .lp-links a:hover { color: #FFB37A; }

        /* footer */
        .lp-footer {
          position: absolute; bottom: 22px; left: 40px; right: 40px;
          font-size: 12px;
          color: rgba(242,240,250,0.5); letter-spacing: 0.04em;
          z-index: 5;
        }

        /* spinner */
        .lp-spinner {
          width: 17px; height: 17px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: lp-spin 0.7s linear infinite;
        }
        @keyframes lp-spin { to { transform: rotate(360deg); } }

        /* mobile card top logo */
        .lp-mobile-logo {
          display: none;
          align-items: center; gap: 10px; margin-bottom: 14px;
        }
        .lp-mobile-logo img { height: 28px; filter: brightness(0) invert(1); }
        .lp-mobile-logo-name { font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 800; color: #fff; }
        @media (max-width: 900px) { .lp-mobile-logo { display: flex; } }
      `}</style>

      <div className="lp-root">
        {/* Showroom photo backdrop */}
        <div className="lp-photo-bg" />

        {/* Top nav */}
        <div className="lp-nav">
          <div className="lp-nav-brand">
            <img src={logo} alt="Rentora" />
            <span className="lp-nav-brand-name">Rentora</span>
          </div>
        </div>

        {/* Two-column body */}
        <div className="lp-body">
          <div className="lp-body-inner">
          {/* Left — headline */}
          <div className="lp-headline-col">
            <span className="lp-eyebrow">{t('loginPageEyebrow')}</span>
            <h1 className="lp-headline">
              {t('loginPageHeadline')}
            </h1>
            <p className="lp-headline-sub">
              {t('loginPageSubtitle')}
            </p>
            <div className="lp-features">
              <div className="lp-feature">
                <span className="lp-feature-icon"><FiCalendar size={15} /></span>
                {t('loginFeaturePlanning')}
              </div>
              <div className="lp-feature">
                <span className="lp-feature-icon"><FiTrendingUp size={15} /></span>
                {t('loginFeatureFinance')}
              </div>
              <div className="lp-feature">
                <span className="lp-feature-icon"><FiShield size={15} /></span>
                {t('loginFeatureSecurity')}
              </div>
            </div>
          </div>

          {/* Right — form card */}
          <div className="lp-card">
            <div className="lp-mobile-logo">
              <img src={logo} alt="Rentora" />
              <span className="lp-mobile-logo-name">Rentora</span>
            </div>
            <div className="lp-card-title">{t('loginFormTitle')}</div>
            <div className="lp-card-subtitle">{t('loginCardSubtitle')}</div>

            <div className="lp-card-body">
              {error && (
                <div className="lp-error">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              {/* Google Sign-In */}
              {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                <>
                  <div id="gsi-button-container" style={{ display: 'none' }} />
                  <button
                    type="button"
                    className="lp-google-btn"
                    disabled={loading}
                    onClick={() => {
                      if (window.google?.accounts?.id) {
                        window.google.accounts.id.prompt();
                      }
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                      <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
                      <path fill="#EA4335" d="M24 9.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 3.13 29.93 1 24 1 15.4 1 7.96 5.93 4.34 13.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
                    </svg>
                    {t('loginWithGoogle')}
                  </button>
                  <div className="lp-divider">{t('loginOrDivider')}</div>
                </>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                <div className="lp-field">
                  <label className="lp-label" htmlFor="lp-username">{t('loginUsernameLabel')}</label>
                  <div className="lp-input-wrap">
                    <span className="lp-input-icon"><FiUser size={15} /></span>
                    <input
                      id="lp-username"
                      className="lp-input"
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      placeholder={t('loginUsernamePlaceholder')}
                      required
                      disabled={loading}
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="lp-field">
                  <label className="lp-label" htmlFor="lp-password">{t('loginPasswordLabel')}</label>
                  <div className="lp-input-wrap">
                    <span className="lp-input-icon"><FiLock size={15} /></span>
                    <input
                      id="lp-password"
                      className="lp-input"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={t('loginPasswordPlaceholder')}
                      required
                      disabled={loading}
                      autoComplete="current-password"
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      className="lp-eye-btn"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? t('loginPasswordVisibilityHide') : t('loginPasswordVisibilityShow')}
                    >
                      {showPassword ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                    </button>
                  </div>
                </div>

                <button className="lp-btn" type="submit" disabled={loading}>
                  {loading
                    ? <><div className="lp-spinner" /> {t('loading')}...</>
                    : <>{t('loginSubmitButton')} <FiLogIn size={14} /></>
                  }
                </button>

                <div className="lp-links">
                  <a
                    href="/forgot-password"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/forgot-password');
                    }}
                  >
                    {t('loginForgotPassword')}
                  </a>
                  <a
                    href="/signup"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/signup');
                    }}
                  >
                    {t('loginRequestAccess')}
                  </a>
                </div>
              </form>
            </div>
          </div>
          </div>
        </div>

        {/* Footer */}
        <div className="lp-footer">© Rentora {new Date().getFullYear()}</div>
      </div>
    </>
  );
};

export default LoginPage;
